/**
 * MentionTextarea — a textarea that shows a @mention autocomplete dropdown.
 * When the user types '@', it opens a user picker filtered by the typed query.
 * On selection it inserts '@FirstName LastName' into the text.
 *
 * Usage:
 *   <MentionTextarea
 *     value={body}
 *     onChange={setBody}
 *     onKeyDown={handleKeyDown}
 *     users={users}
 *     placeholder="Write a comment…"
 *   />
 */
import React, { useRef, useState } from 'react';

interface User { id: string; first_name: string; last_name: string; email: string; }

interface Props {
    value: string;
    onChange: (v: string) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    users: User[];
    placeholder?: string;
}

/** Parse @mentions from text and return an array of spans with highlights.
 *  Mentions are stored as `@[Full Name]` so multi-word names stay whole when
 *  split on whitespace. */
export function renderWithMentions(text: string): React.ReactNode[] {
    const parts = text.split(/(@\[[^\]]+\])/g);
    return parts.map((part, i) => {
        const m = part.match(/^@\[([^\]]+)\]$/);
        return m
            ? <span key={i} style={{ color: 'var(--accent)', fontWeight: 600 }}>@{m[1]}</span>
            : <span key={i}>{part}</span>;
    });
}

export function MentionTextarea({ value, onChange, onKeyDown, users, placeholder }: Props) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [mentionQuery, setMentionQuery] = useState<string | null>(null);
    const [mentionStart, setMentionStart] = useState(0);
    const [activeIdx, setActiveIdx] = useState(0);

    const filtered = mentionQuery !== null
        ? users.filter(u => {
            const full = `${u.first_name} ${u.last_name}`.toLowerCase();
            return full.includes(mentionQuery.toLowerCase()) || u.email.toLowerCase().includes(mentionQuery.toLowerCase());
        }).slice(0, 6)
        : [];

    function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
        const val = e.target.value;
        const pos = e.target.selectionStart ?? val.length;
        onChange(val);

        // Find the @ character before the cursor
        const before = val.slice(0, pos);
        const atIdx = before.lastIndexOf('@');
        if (atIdx !== -1 && !before.slice(atIdx).includes(' ')) {
            setMentionQuery(before.slice(atIdx + 1));
            setMentionStart(atIdx);
            setActiveIdx(0);
        } else {
            setMentionQuery(null);
        }
    }

    function insertMention(u: User) {
        const name = `${u.first_name} ${u.last_name}`.trim() || u.email;
        const before = value.slice(0, mentionStart);
        const after = value.slice(textareaRef.current?.selectionStart ?? value.length);
        onChange(`${before}@[${name}] ${after}`);
        setMentionQuery(null);
        textareaRef.current?.focus();
    }

    function handleKeyDownInternal(e: React.KeyboardEvent<HTMLTextAreaElement>) {
        if (mentionQuery !== null && filtered.length > 0) {
            if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, filtered.length - 1)); return; }
            if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); return; }
            if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(filtered[activeIdx]); return; }
            if (e.key === 'Escape') { setMentionQuery(null); return; }
        }
        onKeyDown?.(e);
    }

    // Heuristic warning: if the user typed `@<word>` (cursor moved past the
    // autocomplete window) but the resulting text doesn't contain `@[Name]`
    // anywhere, they almost certainly tried to mention without picking from
    // the dropdown. Backend won't notify in that case — surface the gotcha
    // inline so it's not a silent failure.
    const hasUnpickedMention = /(?:^|\s)@\w/.test(value) && !/@\[[^\]]+\]/.test(value);

    return (
        <div style={{ position: 'relative' }}>
            <textarea
                ref={textareaRef}
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDownInternal}
                style={{ width: '100%', boxSizing: 'border-box' }}
            />
            <div style={{
                fontSize: 'var(--font-size-xs)',
                color: hasUnpickedMention ? 'var(--warn)' : 'var(--color-text-muted)',
                marginTop: 4,
                lineHeight: 1.4,
            }}>
                {hasUnpickedMention
                    ? '⚠ Looks like you started a mention but didn\'t pick from the list — only picked names trigger a notification. Type @ again and use ↑↓ + Enter to select.'
                    : 'Tip: type @ to mention a teammate, then pick from the list. Only picked names trigger a notification.'}
            </div>
            {mentionQuery !== null && filtered.length > 0 && (
                <div style={{
                    position: 'absolute', bottom: '100%', left: 0, zIndex: 200,
                    background: 'var(--color-surface)', border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)',
                    minWidth: 220, maxHeight: 220, overflowY: 'auto',
                }}>
                    {filtered.map((u, i) => (
                        <button
                            key={u.id}
                            type="button"
                            onMouseDown={e => { e.preventDefault(); insertMention(u); }}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                                width: '100%', border: 'none', cursor: 'pointer', textAlign: 'left',
                                background: i === activeIdx ? 'var(--color-surface-2)' : 'transparent',
                                fontSize: 'var(--font-size-sm)',
                            }}
                        >
                            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--accent)', color: 'var(--on-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
                                {(u.first_name?.[0] || u.email[0]).toUpperCase()}
                            </div>
                            <div>
                                <div style={{ fontWeight: 600 }}>{u.first_name} {u.last_name}</div>
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{u.email}</div>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
